import { useState, useMemo, useEffect } from "react";
import { 
  Card, CardContent, CardHeader, CardTitle, CardDescription 
} from "@/components/ui/card";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Tabs, TabsContent, TabsList, TabsTrigger 
} from "@/components/ui/tabs";
import {
  Search, Users, Activity, History, AlertCircle, CheckCircle, Clock, Eye, Database, UserPlus, Edit, Trash, RefreshCw, ShoppingBag, Package, CalendarCheck, Receipt, Warehouse, FileCheck, ArrowDownUp, ChevronLeft, ChevronRight
} from "lucide-react";
import { format } from "date-fns";
import BASE_URL from "@/constants";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { DateRangeFilter } from "@/components/common/DateRangeFilter";

export default function AuditLogsPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20); // Changed to make it modifiable
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [currentTab, setCurrentTab] = useState("all");
  const [allAuditLogs, setAllAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date } | null>(null);


  const companyName = useSelector((state: any) => state.user.company_name);
  const platform = useSelector((state: RootState) => state.platformOptions.selectedPlatform) || "BlinkIt";

useEffect(() => {
  const client = companyName;
  if (!client || !platform) return;

  setLoading(true);

  const params = new URLSearchParams({
    client_name: client,
    platform: platform,
  });

  if (dateRange?.from && dateRange?.to) {
    params.append('from', dateRange.from.toISOString());
    params.append('to', dateRange.to.toISOString());
  }

  fetch(`${BASE_URL}/api/audit-log/client?${params.toString()}`)
    .then(res => res.json())
    .then(data => {
      setAllAuditLogs(Array.isArray(data) ? data : []);
      setLoading(false);
    })
    .catch(() => {
      setAllAuditLogs([]);
      setLoading(false);
    });
}, [platform, companyName, dateRange]);


    const getActionIcon = (action: string) => {
    switch ((action || "").toLowerCase()) {
      case 'create':
      case 'created':
        return <UserPlus className="h-4 w-4 text-green-600" />;
      case 'update':
      case 'updated':
        return <Edit className="h-4 w-4 text-blue-600" />;
      case 'delete':
      case 'deleted':
        return <Trash className="h-4 w-4 text-red-600" />;
      case 'view':
      case 'viewed':
        return <Eye className="h-4 w-4 text-gray-600" />;
      case 'approve':
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'reject':
      case 'rejected':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'sync':
      case 'synced':
        return <RefreshCw className="h-4 w-4 text-purple-600" />;
      case 'upload':
      case 'uploaded':
        return <ArrowDownUp className="h-4 w-4 text-indigo-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

    const getEntityIcon = () => (
    <ShoppingBag className="h-4 w-4 text-blue-600" />
  );

  const getTeamBadge = (teamName: string) => {
    const teamColors: Record<string, string> = {
      'Planning': 'bg-blue-100 text-blue-800',
      'Appointment': 'bg-green-100 text-green-800',
      'Sales Order': 'bg-purple-100 text-purple-800',
      'Courier': 'bg-orange-100 text-orange-800',
      'Invoicing': 'bg-yellow-100 text-yellow-800',
      'Warehouse': 'bg-gray-100 text-gray-800',
      'Manager': 'bg-red-100 text-red-800'
    };

    return (
      <Badge 
        variant="secondary" 
        className={`${teamColors[teamName] || 'bg-gray-100 text-gray-800'} border-0`}
      >
        {teamName}
      </Badge>
    );
  };

  
  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'MMM dd, yyyy HH:mm:ss');
    } catch {
      return String(timestamp);
    }
  };


  
  // Filter and paginate audit logs
  const filteredLogs = useMemo(() => {
    let filtered = allAuditLogs;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(log =>
        (log.action || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (log.username || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (log.role || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (log.poNumber || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }



    // Apply action filter
    if (actionFilter !== "all") {
      filtered = filtered.filter(log => (log.action || "").toLowerCase() === actionFilter.toLowerCase());
    }

    // Apply team filter (role)
    if (teamFilter !== "all") {
      filtered = filtered.filter(log => (log.role || "").toLowerCase() === teamFilter.toLowerCase());
    }

    return filtered;
  }, [allAuditLogs, searchTerm, actionFilter, teamFilter]);

  // Paginate results
  const auditLogs = useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const items = filteredLogs.slice(startIndex, endIndex);

    return {
      items,
      total: filteredLogs.length,
      totalPages: Math.ceil(filteredLogs.length / limit),
      currentPage: page,
      hasNext: endIndex < filteredLogs.length,
      hasPrevious: page > 1
    };
  }, [filteredLogs, page, limit]);

  const clearFilters = () => {
    setSearchTerm("");
    setActionFilter("all");
    setTeamFilter("all");
    setPage(1);
  };

  const handlePrevious = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNext = () => {
    if (page < auditLogs.totalPages) {
      setPage(page + 1);
    }
  };

  const goToPage = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= auditLogs.totalPages) {
      setPage(pageNumber);
    }
  };

  // ... (keep all your existing helper functions like getActionIcon, getEntityIcon, etc.)

  const totalLogs = auditLogs?.total || 0;
  const currentLogs = auditLogs?.items || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Audit Logs</h1>
          <p className="text-sm text-gray-600 mt-1">
            Track all system activities and changes
          </p>
        </div>
        {/* <Button variant="outline" onClick={clearFilters}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Clear Filters
        </Button> */}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by action, user, team, or PO number..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1); // Reset to first page when searching
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select 
                value={actionFilter} 
                onValueChange={(value) => {
                  setActionFilter(value);
                  setPage(1); // Reset to first page when filter changes
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="updated">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  {/* <SelectItem value="view">View</SelectItem> */}
                  {/* <SelectItem value="approve">Approve</SelectItem> */}
                  {/* <SelectItem value="reject">Reject</SelectItem> */}
                </SelectContent>
              </Select>
              <Select 
                value={teamFilter} 
                onValueChange={(value) => {
                  setTeamFilter(value);
                  setPage(1); // Reset to first page when filter changes
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  <SelectItem value="Planning Team">Planning</SelectItem>
                  <SelectItem value="Appointment Team">Appointment</SelectItem>
                  <SelectItem value="Sales Order Team">Sales Order</SelectItem>
                  <SelectItem value="Courier Team">Courier</SelectItem>
                  <SelectItem value="Invoicing Team">Invoicing</SelectItem>
                  <SelectItem value="Warehouse Team">Warehouse</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                </SelectContent>
              </Select>
              <Select 
                value={limit.toString()} 
                onValueChange={(value) => {
                  setLimit(Number(value));
                  setPage(1); // Reset to first page when page size changes
                }}
              >
                <SelectTrigger className="w-20">
                  <SelectValue placeholder="Rows" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab}>
        <TabsList className="flex justify-between">
          <TabsTrigger value="all" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            All Logs ({totalLogs})
          </TabsTrigger>
             <DateRangeFilter onDateRangeChange={setDateRange} />
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                All Audit Logs
              </CardTitle>
              <CardDescription>
                Complete system activity log with filtering and search capabilities
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center text-gray-500 py-10">Loading...</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead>Team</TableHead>
                        <TableHead>Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentLogs.length > 0 ? (
                        currentLogs.map((log) => (
                          <TableRow key={log._id}>
                            <TableCell className="text-sm text-gray-600">
                              {formatTimestamp(log.createdAt)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-gray-400" />
                                <span className="font-medium">{log.username}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getActionIcon(log.action)}
                                <span>{log.action}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getEntityIcon()}
                                <span>Purchase Order</span>
                                <span className="text-xs text-gray-500">#{log.PO_ID}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {log.role && getTeamBadge(log.role)}
                            </TableCell>
                            <TableCell className="max-w-xs">
                              <div className="text-sm text-gray-600 truncate">
                                PO Number: {log.poNumber}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                            No audit logs found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>

                  {/* Pagination Controls */}
                  {auditLogs.totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-gray-600">
                        Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalLogs)} of {totalLogs} entries
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handlePrevious}
                          disabled={!auditLogs.hasPrevious}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        
                        {/* Page numbers */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, auditLogs.totalPages) }, (_, i) => {
                            let pageNumber;
                            if (auditLogs.totalPages <= 5) {
                              pageNumber = i + 1;
                            } else if (page <= 3) {
                              pageNumber = i + 1;
                            } else if (page >= auditLogs.totalPages - 2) {
                              pageNumber = auditLogs.totalPages - 4 + i;
                            } else {
                              pageNumber = page - 2 + i;
                            }
                            
                            return (
                              <Button
                                key={pageNumber}
                                variant={page === pageNumber ? "default" : "outline"}
                                size="sm"
                                onClick={() => goToPage(pageNumber)}
                              >
                                {pageNumber}
                              </Button>
                            );
                          })}
                          
                          {auditLogs.totalPages > 5 && page < auditLogs.totalPages - 2 && (
                            <span className="px-2">...</span>
                          )}
                          
                          {auditLogs.totalPages > 5 && page < auditLogs.totalPages - 2 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => goToPage(auditLogs.totalPages)}
                            >
                              {auditLogs.totalPages}
                            </Button>
                          )}
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleNext}
                          disabled={!auditLogs.hasNext}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}